import type { ActivationStore } from '../model/store/interfaces'
import type { ActivationRequestData } from '@brighterly/lib-core-types/activation/interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'
import { StartDateType } from '@brighterly/lib-core-types/activation/enums'
import { Stage } from '@brighterly/lib-core-types/discipline/enums'

import dayjs from 'dayjs'

import { useCurrentTime } from '@entities/customer/@x/activation'

import { getScheduleList } from './schedule-list'

// Backend `DisciplineActivationResource` returns `start_date` as
// `Y-m-d H:i:s` while the POST /activation validator only accepts `Y-m-d`.
// On any refresh mid-flow, the discipline-info refetch hands us the datetime
// form, which then round-trips into the next step's payload and 422s. Strip
// the time portion at the wire boundary so the validator stays happy.
// Behavior-preserving for the happy path: 'Y-m-d' input → 'Y-m-d' output.
const toDateOnly = (value: string): string => {
  if (!value) return ''
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format(DateFormat.DefaultDate) : value
}

export const getActivationRequestData = (state: ActivationStore): ActivationRequestData => {
  const schedule = getScheduleList(state.schedule)
  const expectations = toRaw(state.expectations) || []
  const preferences = toRaw(state.preferences) || ''
  const notificationChannels = toRaw(state.activationReminders) || []
  const soon = state.switcherStartDateType === StartDateType.Soon

  const {
    currentDate,
  } = useCurrentTime()

  const startDate = soon
    ? currentDate.value
    : toDateOnly(state.startDate || '')
  const screenRecording = toRaw(state.screenRecording) || false
  const stage = toRaw(state.stage) || Stage.ManualActivation

  return {
    discipline: {
      activation_data: {
        schedule,
        start_date: startDate,
        start_date_type: state.switcherStartDateType,
        details: {
          expectations,
          preferences,
        },
      },
      stage,
    },
    customer: {
      contact_data: {
        notification_channels: notificationChannels,
      },
      screen_recording: screenRecording,
    },
  }
}
